<script lang="ts">
  import { createEventDispatcher, onMount } from "svelte";
  import BlurHashCanvas from "$lib/components/Picture/BlurhashCanvas.svelte";
  import StaticImage from "../StaticImage/StaticImage.svelte";
  import type { CropRegion } from "./Image";

  export let smImage: string;
  export let mdImage: string;
  export let lgImage = mdImage;
  export let xlImage = lgImage || mdImage;
  export let altText: string;
  export let imageConfig = {
    smSize: { width: 767, height: 767 },
    mdSize: { width: 1199, height: 782 },
    lgSize: { width: 1200, height: 480 },
    xlSize: { width: 1600, height: 480 },
  };

  export let fitOrFill: string = "fit";

  export let cover = false;

  export let noTransform = false;

  export let shopifyImage = false;

  $: if (smImage || mdImage) {
    lgImage = mdImage;
    xlImage = lgImage || mdImage;
  }

  let uuidPattern =
    /(?<!\/)(?:\b[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}\b)/g;

  let isFliteImage =
    smImage?.includes("flite") ||
    smImage?.match(uuidPattern) ||
    mdImage?.includes("flite") ||
    mdImage?.match(uuidPattern) ||
    false;

  const dispatch = createEventDispatcher();
  let imgEl: HTMLImageElement;

  let isVisible = false;
  let imageLoaded = false;

  const imageIsLoaded = (e?: Event) => {
    imageLoaded = true;
    e && dispatch("imageLoaded", e);
  };

  onMount(() => {
    if (imgEl && imgEl.complete) {
      imageIsLoaded();
    }

    if (observer) {
      observer.observe(container);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  });

  $: if (smImage) {
    imageKey = `${smImage}-${mdImage}-${lgImage}-${xlImage}`;
  }

  function extractBlurhash(image: string) {
    const blurhash = image.match(/blurhash=([^&]*)/);
    if (blurhash && blurhash[1]) {
      return blurhash[1];
    }
    return null;
  }

  function generatePlaceholderImage(image: string) {
    if (shopifyImage) {
      return `${image}?width=40`;
    }

    const [base, rest] = image?.split("/upload/") || ["", ""];
    const parts = rest?.split("/");
    const versionOrIdIndex = parts?.findIndex(part => part.startsWith("v"));

    const versionAndId = parts?.slice(versionOrIdIndex).join("/");
    return `${base}/upload/f_auto,q_auto${
      noTransform ? "" : `,c_fit,w_${40}`
    }/${versionAndId}`;
  }

  function transformImage(
    image: string,
    size: { width: number; height: number; crop?: CropRegion },
    doubleSize = false,
  ): string {
    const width = doubleSize ? size.width * 2 : size.width;
    const height = doubleSize ? size.height * 2 : size.height;

    if (shopifyImage) {
      const basePath = image.split("?")[0]; // Get the base URL without query params
      const queryParams = new URLSearchParams(image.split("?")[1]);
      const fileExtension = basePath.split(".").pop() || "jpg";
      const baseWithoutExt = basePath.substring(0, basePath.lastIndexOf("."));
      return `${baseWithoutExt}_${width}x${height}${
        size.crop ? `_crop_${size.crop}` : ""
      }.${fileExtension}?${queryParams.toString()}`;
    }

    const [base, rest] = image?.split("/upload/") || ["", ""];
    const parts = rest?.split("/");
    const versionOrIdIndex = parts?.findIndex(part => part.startsWith("v"));

    const versionAndId = parts?.slice(versionOrIdIndex).join("/");
    return `${base}/upload/f_auto,q_80${
      noTransform ? "" : `,c_${fitOrFill},w_${width},h_${height}`
    }/${versionAndId}`;
  }

  $: mdBlurhash = extractBlurhash(mdImage);
  $: smBlurhash = extractBlurhash(smImage);

  $: placeholderImageSm = generatePlaceholderImage(smImage);
  $: placeholderImage = generatePlaceholderImage(mdImage);

  $: xlSrcSet = `${transformImage(
    xlImage,
    imageConfig.xlSize,
  )} 1x, ${transformImage(xlImage, imageConfig.xlSize, true)} 2x`;
  $: lgSrcSet = `${transformImage(
    lgImage,
    imageConfig.lgSize,
  )} 1x, ${transformImage(lgImage, imageConfig.lgSize, true)} 2x`;
  $: mdSrcSet = `${transformImage(
    mdImage,
    imageConfig.mdSize,
  )} 1x, ${transformImage(mdImage, imageConfig.mdSize, true)} 2x`;
  $: smSrcSet = `${transformImage(
    smImage,
    imageConfig.smSize,
  )} 1x, ${transformImage(smImage, imageConfig.smSize, true)} 2x`;

  $: fallbackSrc = transformImage(smImage, imageConfig.smSize);
  $: imageKey = `${smImage}-${mdImage}-${lgImage}-${xlImage}`;

  const onVisible = () => {
    isVisible = true;

    if (imgEl && imgEl.complete) {
      imageIsLoaded();
    }

    dispatch("visible");
  };

  let observer: IntersectionObserver;
  let container: HTMLPictureElement;

  export function lazyLoad(node: HTMLElement) {
    observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            onVisible(); // call passed-in function
            observer.unobserve(node);
          }
        });
      },
      {
        rootMargin: "350px",
      },
    );

    observer.observe(node);

    return {
      destroy() {
        observer.disconnect();
      },
    };
  }
</script>

<style lang="scss">
  .cover {
    height: 100%;
    width: 100%;
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
    }
  }

  img {
    display: block;
    width: 100%;
    height: auto;
    max-width: 100%;
    opacity: 0;
    transition: opacity 0.3s ease-in-out;
  }

  img.image-loaded {
    opacity: 1;
  }

  picture {
    position: relative;
    display: block;
  }

  .placeholder-image {
    filter: blur(20px);
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    transform: scale(1.1);
    z-index: 10;
    opacity: 1 !important;
    transition: opacity 0.3s ease;

    &__mobile {
      display: block;
    }
    @include breakpoint(sm) {
      &__mobile {
        display: none;
      }
      & {
        display: block;
      }
    }
  }
</style>

{#if isFliteImage}
  <StaticImage
    {smImage}
    {mdImage}
    {lgImage}
    {xlImage}
    {altText}
    smSize={imageConfig.smSize}
    mdSize={imageConfig.mdSize}
    lgSize={imageConfig.lgSize}
    xlSize={imageConfig.xlSize}
    {cover}
  />
{:else}
  <div class="inner">
    {#if !imageLoaded && isVisible}
      {#if mdBlurhash || smBlurhash}
        <BlurHashCanvas hash={mdBlurhash} {smBlurhash} width={32} height={32} />
      {:else}
        <img
          id="placeholderImage__mobile"
          width={40}
          src={placeholderImageSm}
          alt={altText}
          class="placeholder-image__mobile"
        />
        <img
          id="placeholderImage"
          width={40}
          src={placeholderImage}
          alt={altText}
          class="placeholder-image"
        />
      {/if}
    {/if}
    <picture class={cover ? "cover" : ""} bind:this={container} use:lazyLoad>
      {#if isVisible || imageLoaded}
        <source media="(max-width: 767px)" srcset={smSrcSet} />
        <source
          media="(min-width: 768px) and (max-width: 1279px)"
          srcset={mdSrcSet}
          sizes="50vw"
        />
        <source
          media="(min-width: 1280px) and (max-width: 1399px)"
          srcset={lgSrcSet}
          sizes="33vw"
        />
        <source media="(min-width: 1400px)" srcset={xlSrcSet} sizes="25vw" />
        <img
          id="staticImage"
          bind:this={imgEl}
          src={fallbackSrc}
          width={imageConfig.smSize.width}
          height={imageConfig.smSize.height}
          alt={altText}
          class:image-loaded={imageLoaded}
          on:load={e => imageIsLoaded(e)}
        />
      {:else}
        <!-- svelte-ignore a11y-missing-attribute -->
        <img
          id="staticImage-lazy"
          width={imageConfig.smSize.width}
          height={imageConfig.smSize.height}
        />
      {/if}
    </picture>
  </div>
{/if}
